/**
 * Gold insoles product form (v13).
 *
 * HOW THE VARIANT IS FOUND
 * The wizard collects five answers (gender, sport, size, weight, age) but Shopify only allows
 * three variant options, and the gold insole is actually six separate products (pro levels
 * L1/L3/L4/L5/L6, exposed as globals by theme.liquid, or reassigned by the section when
 * use_alt_gold is enabled). Resolution works in two steps:
 *   1. Pick the product: goldWeightToProduct maps the weight bracket to a pro level, then
 *      resolveTargetProduct applies two overrides (Female at 151-180lbs steps down to L3,
 *      and age "50 and Up" drops one pro level per weight bracket).
 *   2. Pick the variant inside that product: option1 is a size range like "5-5.5" (split on
 *      "-" and matched exactly), option2 is the top cover letter (A/B/C, derived from the
 *      sport via sportToTopCover), option3 is the gender.
 * Sport, weight and age also ship as line item properties; only size/top cover/gender are
 * real variant options.
 *
 * HOW DISABLING WORKS
 * Instead of waiting for all five answers and erroring at the end, every choice that leads
 * ONLY to variants we do not carry is grayed out (class "soldOut" on wizard labels, native
 * disabled on the pair modal select options). The core predicate is canComplete(partial):
 * unanswered groups are treated as wildcards and enumerated, so a choice is grayed only
 * when NO completion of the remaining answers ends at a variant we carry. Stock is NOT part
 * of that test, so a choice that can only reach sold out variants stays ungrayed on purpose.
 * Because picking an earlier answer clears all later answers, each label is constrained only
 * by answers on slides BEFORE it (the modal applies the same order and auto-resets later
 * selects instead). The first slide (gender) is never grayed. Clicks on grayed labels are
 * ignored, so the wizard can only be completed on a combination we carry. A carried but sold
 * out combination is never grayed, so it stays clickable and reaches the end of the flow,
 * where updateProduct swaps the ATC for a "Notify me when back in stock" button (class "OOS").
 * The end-of-flow availability check in updateProduct stays as the backstop for stale pages.
 */
(function () {

    // save the section id from the script tag and find the section
    const sectionID = document.currentScript.dataset.id;
    const section = document.querySelector(`[id="${sectionID}"]`);

    // exit if section is not found
    if (!section) {
        console.warn(`Product section with ID ${sectionID} not found.`);
        return;
    }

    const product = JSON.parse(section.querySelector(".productV13FormJSON").textContent);

    // get orders sold
    fetch("https://cdn.vktry.com/order-count.json")
        .then(response => response.json())
        .then(response => {
            // show badge only if there are orders
            if (response.gold_order_count && response.gold_order_count > 0) {
                // format number with commas
                const ordersSoldCommas = response.gold_order_count.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

                section.querySelector(".productV13ProofSoldTxt").innerHTML = `${ordersSoldCommas} Sold In Last 7 Days`;
                section.querySelector(".productV13ProofSold").classList.add("active");
            }
        })


    // get review totals, the okendo product id is rendered on the rating button
    const reviewsProductID = section.querySelector(".productV13Rating")?.dataset.okeReviewsProductId?.replace("shopify-", "");
    if (reviewsProductID) {
        getReviews(reviewsProductID).then(reviews => {
            // the liquid rendered values stay put when okendo is unreachable
            if (!reviews) {
                return;
            }

            section.querySelector(".productV13RatingScore").textContent = reviews.average;
            section.querySelector(".productV13RatingTxt").textContent = `${reviews.count.toLocaleString()} verified reviews`;
            section.querySelector(".productV13Rating").classList.add("active");
        });
    }

    let thumbsSwiper = new Swiper(section.querySelector(".productV13GalleryThumbs"), {
        slidesPerView: 6,
        spaceBetween: 6,
        watchSlidesProgress: true
    })

    // set up images swiper
    let productSwiper = new Swiper(section.querySelector(".productV13GalleryStageSwiper"), {
        slidesPerView: 1,
        spaceBetween: 50,
        thumbs: {
            swiper: thumbsSwiper, // Pass the thumbs instance here
        },
    });

    // keep the highlighted thumbnail in sync with whichever slide is showing
    productSwiper.on("slideChange", function () {
        section.querySelectorAll(".productV13GalleryThumbsItem").forEach((element, index) => {
            element.classList.toggle("active", index === productSwiper.activeIndex);
        });

        // drop the tilt from the slide we left so it does not freeze mid rotation
        section.querySelectorAll(".productV13GalleryStageSwiperItemImg").forEach(element => element.style.removeProperty("transform"));
    });

    // change slide on thumbnail click
    section.querySelectorAll(".productV13GalleryThumbsItem").forEach(element => {
        element.addEventListener("click", function () {
            // change slide
            productSwiper.slideTo(this.dataset.index - 1);
        });
    });

    // highlight the thumbnail the gallery opens on
    section.querySelector(".productV13GalleryThumbsItem")?.classList.add("active");

    /************
     Gallery Tilt
     ************/

        // the insole leans toward the pointer and bobs, the way the product stage does in the design
    const galleryStage = section.querySelector(".productV13GalleryStage");
    const tilt = {x: 0, y: 0, targetX: 0, targetY: 0};

    // the tilt only applies to photo slides, and flat infographics opt out of it
    function activeGalleryImage() {
        return productSwiper.slides[productSwiper.activeIndex]?.querySelector(".productV13GalleryStageSwiperItemImg:not(.flat)") ?? null;
    }

    function renderGalleryTilt(time) {
        const image = activeGalleryImage();

        if (image) {
            // ease toward the pointer so the insole trails it rather than snapping
            tilt.x += (tilt.targetX - tilt.x) * 0.08;
            tilt.y += (tilt.targetY - tilt.y) * 0.08;

            const bob = Math.sin(time / 900) * 5;
            image.style.transform = `translateY(${bob}px) perspective(1200px) rotateX(${tilt.y * -4}deg) rotateY(${tilt.x * 8}deg) rotateZ(${tilt.x * 3}deg)`;
        }

        requestAnimationFrame(renderGalleryTilt);
    }

    // skip the whole loop when the visitor asked for less motion
    if (galleryStage && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        galleryStage.addEventListener("pointermove", function (event) {
            const bounds = galleryStage.getBoundingClientRect();
            tilt.targetX = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
            tilt.targetY = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
        });

        // settle back to centre once the pointer leaves the stage
        galleryStage.addEventListener("pointerleave", function () {
            tilt.targetX = 0;
            tilt.targetY = 0;
        });

        requestAnimationFrame(renderGalleryTilt);
    }


    // update active variant
    let formData = {
        items: []
    };

    // top cover is based off of sport (GOLD INSOLES)
    const sportToTopCover = {
        "Baseball/Softball": "B",
        "Basketball": "A",
        "Ultimate Frisbee": "B",
        "Wrestling/Boxing": "B",
        "Cheerleading": "C",
        "Fitness": "A",
        "Golf": "B",
        "Hiking": "A",
        "Football": "B",
        "Lacrosse": "B",
        "Rugby": "B",
        "Running": "A",
        "Soccer": "C",
        "Tennis/Pickleball": "A",
        "Track & Field": "C",
        "Volleyball": "A",
        "Walking": "A",
        "Other": "A",
        "Ice Hockey": "C",
    }

    /********************* Variant Resolution *********************/

    /**
     * Check a variant against partial filters, null fields act as wildcards.
     * @param {Object} variant Shopify variant JSON (option1 size range, option2 top cover, option3 gender)
     * @param {{size: ?string, topCover: ?string, gender: ?string}} filters values to match, null matches anything
     * @returns {boolean} true when the variant satisfies every non-null filter
     */
    function variantMatches(variant, filters) {
        // size ranges like "5-5.5" are split so the exact size can be matched
        if (filters.size !== null && !variant.option1.split("-").includes(filters.size)) {
            return false;
        }

        // top cover letter
        if (filters.topCover !== null && variant.option2 !== filters.topCover) {
            return false;
        }

        // gender
        if (filters.gender !== null && variant.option3 !== filters.gender) {
            return false;
        }

        return true;
    }

    /**
     * Find the exact variant for a full selection.
     * @param {Object} targetProduct Shopify product JSON returned by resolveTargetProduct
     * @param {string} size shoe size, e.g. "5" (matched against option1 ranges like "5-5.5")
     * @param {string} topCover top cover letter "A" | "B" | "C" from sportToTopCover
     * @param {string} gender "Male" | "Female"
     * @returns {?Object} the matching variant, or null when the combination is not carried
     */
    function findVariant(targetProduct, size, topCover, gender) {
        return targetProduct.variants.find(variant => variantMatches(variant, {size, topCover, gender})) ?? null;
    }

    /**
     * Check if a product carries any variant matching the (possibly partial) filters.
     * Stock is deliberately ignored: a sold out variant is still one we carry, so it must not
     * gray out the choice that leads to it.
     * @param {Object} targetProduct Shopify product JSON
     * @param {{size: ?string, topCover: ?string, gender: ?string}} filters values to match, null matches anything
     * @returns {boolean} true when at least one matching variant exists, in stock or not
     */
    function hasCarriedVariant(targetProduct, filters) {
        return targetProduct.variants.some(variant => variantMatches(variant, filters));
    }

    /**
     * Resolve which pro level product a weight/age/gender combination routes to.
     * @param {string} weight weight bracket key of goldWeightToProduct, e.g. "151 - 180lbs"
     * @param {string} age age bracket, e.g. "21 - 30" ("50 and Up" drops one pro level)
     * @param {string} gender "Male" | "Female" (Female at 151-180lbs steps down to L3)
     * @returns {Object} the pro level product JSON (goldProductL1/L3/L4/L5/L6)
     */
    function resolveTargetProduct(weight, age, gender) {
        // base product comes from the weight bracket
        let targetProduct = goldWeightToProduct[weight];

        // Note we do not touch the weight variable since it's used as a line item property
        // if they are Female and size 151-180, it changes the weight to pro level 3 from level 4 (we have another case for ages 50 and up so we exclude that)
        if (gender === "Female" && weight === "151 - 180lbs" && age !== "50 and Up") {
            targetProduct = goldWeightToProduct["121 - 150lbs"];
        }

        // if they are age 50 & up, we decrease the pro level by 1
        if (age === "50 and Up") {
            if (weight === "261lbs - Up") {
                targetProduct = goldWeightToProduct["231 - 260lbs"];
            } else if (weight === "231 - 260lbs" || weight === "211 - 230lbs") {
                targetProduct = goldWeightToProduct["181 - 210lbs"];
            } else if (weight === "181 - 210lbs" || weight === "151 - 180lbs") {
                targetProduct = goldWeightToProduct["121 - 150lbs"];
            } else if (weight === "121 - 150lbs") {
                targetProduct = goldWeightToProduct["100 - 120lbs"];
            } else {
                targetProduct = goldWeightToProduct["100 - 120lbs"];
            }
        }

        return targetProduct;
    }

    /**
     * Turn a full wizard selection into a cart line item.
     * @param {{gender: string, sport: string, size: string, weight: string, age: string}} options one pair's answers
     * @param {string} targetHandle product handle, kept for call-site parity across insole scripts
     * @returns {Object|false} {quantity, id, properties, available}, or false when no variant matches
     */
    function getFormData(options, targetHandle) {
        // gender is a variant (make sure something is checked)
        let gender = options.gender ?? null;

        // sport is a property
        let sport = options.sport ?? null;

        // size is a variant
        let size = options.size ?? null;

        // weight is a property
        let weight = options.weight ?? null;

        // age is a property
        let age = options.age ?? null;

        // Determine product (weight/age/gender overrides live in resolveTargetProduct)
        let targetProduct = resolveTargetProduct(weight, age, gender);

        // Determine Top Cover
        let topCover = sportToTopCover[sport];

        // find the correct variant based on the active options
        let activeVariant = findVariant(targetProduct, size, topCover, gender);

        // check if active variant is not found in any setup
        if (activeVariant == null) {
            return false;
        }

        // gather item
        let formData = {
            quantity: 1,
            id: activeVariant.id,
            properties: {
                "Age": age,
                "Sport": sport,
                "Weight": weight
            },
            available: activeVariant.available
        };

        // return
        return formData;

    }

    /**************
     Product Slider
     ***************/

        // enable swiper
    let productFormSwiper = new Swiper(".productV13FormSwiper", {
            allowTouchMove: false,
            autoHeight: true,
            spaceBetween: 30
        });


    /********************* Wizard Navigation *********************/

        // the wizard asks one question at a time, so only the step on screen may hold focus
    function syncSlideFocus(moveFocus) {
        const slides = section.querySelectorAll(".productV13FormOptionSlide");
        slides.forEach((element, index) => element.toggleAttribute("inert", index !== productFormSwiper.activeIndex));

        // land keyboard and screen reader users on the new question instead of leaving focus behind
        if (moveFocus) {
            slides[productFormSwiper.activeIndex]?.focus({preventScroll: true});
        }
    }

    // next stays locked until the step on screen has an answer, so steps cannot be skipped
    function refreshNavState() {
        const slide = section.querySelectorAll(".productV13FormOptionSlide")[productFormSwiper.activeIndex];
        const next = slide?.querySelector(".productV13FormSwiperNavNext");

        if (next) {
            next.disabled = !slide.querySelector(".productV13FormOptionSlideLabel.active");
        }
    }

    // steps past the furthest one reached stay locked, so the progress bar cannot jump ahead either
    let maxSlideReached = 0;

    function refreshProgressState() {
        maxSlideReached = Math.max(maxSlideReached, productFormSwiper.activeIndex);
        section.querySelectorAll(".productV13FormProgressCircleInner").forEach((element, index) => {
            element.disabled = index > maxSlideReached;
        });
    }

    // bound once here rather than on every slide change, which used to stack a listener per visit
    section.querySelectorAll(".productV13FormProgressCircleInner").forEach((element, index) => {
        element.addEventListener("click", function () {
            productFormSwiper.slideTo(index);
        });
    });

    // Progress bar
    productFormSwiper.on("slideChange", function () {
        // update active progress bar circles
        section.querySelectorAll(".productV13FormProgressCircle")?.forEach((element, index) => {
            if (index <= productFormSwiper.activeIndex) {
                // we need to wait to add the active class
                // because the progress bar takes 300ms to animate
                setTimeout(() => {
                    element.classList.add("active");
                }, 100);
            } else {
                element.classList.remove("active");
            }
        });

        // update active progress bar lines
        section.querySelectorAll(".productV13FormProgressLineInner").forEach((element, index) => {
            element.classList.remove("active");
            if (index < productFormSwiper.activeIndex) {
                element.classList.add("active");
            }
        });

        // recompute disabled options so back navigation always shows current dead ends
        refreshDisabledOptions();

        // keep focus, the next button and the progress bar in step with the slide on screen
        syncSlideFocus(true);
        refreshNavState();
        refreshProgressState();
    });

    // seal off the off screen steps before the visitor can tab into them
    syncSlideFocus(false);


    // Progress bar final slide edge cases
    // The final form is the last "slide" but isn't a swiper slide.
    // We need to add the active class to the final progress bar/circle when
    // the last swiper slide option is clicked, which is the weight group for platinum
    // and is the age group for silver/gold.
    let targetGroup = "age"


    section.querySelectorAll(`.productV13FormOptionSlideLabel[data-group='${targetGroup}']`)?.forEach(element => {
        element.addEventListener("click", function () {
            // ignore blocked choices so the progress bar only fills on real selections
            if (this.classList.contains("soldOut")) {
                return;
            }

            // add active class to all progress bar circles
            section.querySelectorAll(".productV13FormProgressCircle")?.forEach((element, index) => {
                element.classList.add("active");
            });
            // add active class to all progress bar lines
            section.querySelectorAll(".productV13FormProgressLineInner")?.forEach((element, index) => {
                element.classList.add("active");
            });

            // Since we're on the last slide, we need to disable the buttons
            section.querySelectorAll('.productV13FormProgressCircleInner').forEach((element) => {
                element.disabled = true;
            });
        });
    });


    // update active variant on click
    section.querySelectorAll(".productV13FormOptionSlideLabel").forEach(function (element) {
        element.addEventListener("click", function () {
            // ignore choices that lead only to variants we do not carry
            if (this.classList.contains("soldOut")) {
                return;
            }

            const group = this.dataset.group;
            const value = this.dataset.value;

            // when an earlier answer changes, clear all later answers so stale picks cannot linger
            const previousActive = section.querySelector(`.productV13FormOptionSlideLabel[data-group="${group}"].active`);
            if (previousActive && previousActive.dataset.value !== value) {
                optionGroups.slice(optionGroups.indexOf(group) + 1).forEach(laterGroup => {
                    section.querySelectorAll(`.productV13FormOptionSlideLabel[data-group="${laterGroup}"].active`).forEach(element => {
                        element.classList.remove("active");
                        element.setAttribute("aria-pressed", "false");
                    });
                });
            }

            // remove active class from all items in this group
            section.querySelectorAll(`.productV13FormOptionSlideLabel[data-group="${group}"]`).forEach(element => {
                element.classList.remove("active");
                element.setAttribute("aria-pressed", "false");
            });

            // add active class to this item
            this.classList.add("active");
            this.setAttribute("aria-pressed", "true");

            // the answer unlocks next straight away, before the slide transition runs
            refreshNavState();

            // recompute which upcoming choices are dead ends for the new selection
            refreshDisabledOptions();

            // go to next slide
            productFormSwiper.slideNext();


            // check if every group has an active item
            if (section.querySelectorAll(".productV13FormOptionSlide").length === section.querySelectorAll(".productV13FormOptionSlideLabel.active").length) {
                section.querySelector(".productV13FormSwiper").classList.remove("active");
                section.querySelector(".productV13FormForm").classList.add("active");

                // the wizard just hid itself, so focus would otherwise fall back to the body
                section.querySelector(".productV13FormFormTitle").focus({preventScroll: true});
            } else {
                section.querySelector(".productV13FormSwiper").classList.add("active");
                section.querySelector(".productV13FormForm").classList.remove("active");
                return;
            }

            // create options object
            let options = {};
            section.querySelectorAll(`.productV13FormOptionSlideLabel.active`).forEach(element => {
                options[element.dataset.group] = element.dataset.value;
            });

            // save options
            section.querySelector(".productV13FormPair").setAttribute("options", JSON.stringify(options));
            section.querySelector(".productV13FormPair").classList.add("active");


            // update product
            updateProduct();


        });
    })


    // back button
    section.querySelectorAll(".productV13FormSwiperNavBack").forEach(function (element) {
        element.addEventListener("click", function () {
            productFormSwiper.slidePrev();
        });
    });

    // next button
    section.querySelectorAll(".productV13FormSwiperNavNext").forEach(function (element) {
        element.addEventListener("click", function () {
            productFormSwiper.slideNext();
        });
    });

    /************
     Why We Ask
     ************/

    // the section fires its own dialog instead of the shared toolTipV2 so the styling can match the card
    section.querySelectorAll(".productV13FormOptionSlideTitleWhy").forEach(function (element) {
        element.addEventListener("click", function () {
            Swal.fire({
                customClass: {
                    container: "productV13Why"
                },
                title: this.dataset.title,
                text: this.dataset.text,
                confirmButtonText: "OK"
            });
        });
    });

    /********************* Disabled Options *********************/

        // slide order used to clear later answers when an earlier one changes
    const optionGroups = ["gender", "sport", "size", "weight", "age"];

    // possible gender/age values come from the rendered labels so Liquid stays the source of truth
    const genderValues = Array.from(section.querySelectorAll(`.productV13FormOptionSlideLabel[data-group="gender"]`)).map(element => element.dataset.value);
    const ageValues = Array.from(section.querySelectorAll(`.productV13FormOptionSlideLabel[data-group="age"]`)).map(element => element.dataset.value);

    /**
     * Read the currently selected wizard answers into a partial options object.
     * @returns {Object} answered groups only, e.g. {gender: "Female", sport: "Basketball"}
     */
    function getSelectedOptions() {
        let options = {};
        section.querySelectorAll(".productV13FormOptionSlideLabel.active").forEach(element => {
            options[element.dataset.group] = element.dataset.value;
        });
        return options;
    }

    /**
     * Check if a partial selection can still end at a variant we carry, sold out or not.
     * Missing answers are wildcards: they are enumerated over every value they could still take.
     * @param {{gender?: string, sport?: string, size?: string, weight?: string, age?: string}} options partial selection
     * @returns {boolean} true when at least one completion of the missing answers is a variant we carry
     */
    function canComplete(options) {
        // assume possible on templates that do not render the gold product globals
        if (typeof goldWeightToProduct === "undefined") {
            return true;
        }

        // unknown answers are enumerated over every value they could still take
        const genders = options.gender ? [options.gender] : genderValues;
        const weights = options.weight ? [options.weight] : Object.keys(goldWeightToProduct);
        const ages = options.age ? [options.age] : ageValues;
        const topCover = options.sport ? sportToTopCover[options.sport] : null;

        // possible iff any routing triple resolves to a product that carries a matching variant
        return genders.some(gender => weights.some(weight => ages.some(age =>
            hasCarriedVariant(resolveTargetProduct(weight, age, gender), {size: options.size ?? null, topCover, gender})
        )));
    }

    /**
     * Gray out every wizard choice that can only lead to variants we do not carry.
     * Sold out variants are still carried, so they never gray a choice out.
     * Clicking a label clears every later answer, so each label is constrained only by
     * answers on slides before it, and the first slide is never grayed.
     */
    function refreshDisabledOptions() {
        const selected = getSelectedOptions();

        section.querySelectorAll(".productV13FormOptionSlideLabel").forEach(element => {
            const group = element.dataset.group;

            // the first slide always stays fully clickable
            if (group === optionGroups[0]) {
                element.classList.remove("soldOut");
                element.disabled = false;
                return;
            }

            // clicking a label clears every later answer, so only earlier answers constrain it
            let options = {[group]: element.dataset.value};
            optionGroups.slice(0, optionGroups.indexOf(group)).forEach(earlierGroup => {
                if (selected[earlierGroup]) {
                    options[earlierGroup] = selected[earlierGroup];
                }
            });

            const blocked = !canComplete(options);
            element.classList.toggle("soldOut", blocked);

            // disabled keeps blocked choices out of the tab order and announces them as unavailable
            element.disabled = blocked;
        });
    }

    /**
     * Disable dead-end choices inside the pair modal and clear answers that just became invalid.
     * Mirrors refreshDisabledOptions: each group is constrained only by the groups before it in
     * the wizard order, and a later answer that became unreachable is cleared rather than kept.
     */
    function refreshPairModalDisabledOptions() {
        const current = getPairModalOptions();

        // disable every choice that can no longer end at a variant we carry
        section.querySelectorAll(".productV13FormPairModalBodyGroupValuesItem").forEach(element => {
            const group = element.dataset.group;

            // invalid later answers are cleared below, so only earlier answers constrain a group
            let earlierOptions = {};
            optionGroups.slice(0, optionGroups.indexOf(group)).forEach(earlierGroup => {
                if (current[earlierGroup]) {
                    earlierOptions[earlierGroup] = current[earlierGroup];
                }
            });

            const blocked = !canComplete({...earlierOptions, [group]: element.dataset.value});
            element.classList.toggle("soldOut", blocked);
            element.disabled = blocked;
        });

        // clear any answer that just became unreachable, then re-check with the widened answers
        let cleared = false;
        section.querySelectorAll(".productV13FormPairModalBodyGroupValuesItem.active").forEach(element => {
            if (element.disabled) {
                setPairModalItem(element, false);
                cleared = true;
            }
        });
        if (cleared) {
            refreshPairModalDisabledOptions();
            return;
        }

        // the save button waits on every group being answered
        const answered = Object.keys(getPairModalOptions()).length === optionGroups.length;
        pairModal.querySelector(".productV13FormPairModalFooterSave").disabled = !answered;
    }

    // initial pass so globally dead choices (e.g. sizes no product carries) are grayed from the start
    refreshDisabledOptions();

    /*******************
     * Product Pairs
     */

        // one modal serves every pair, so it tracks which pair it is editing
    const pairModal = section.querySelector(".productV13FormPairModal");
    let pairModalTarget = null;

    // reflect a choice in both the class list and the state assistive tech reads
    function setPairModalItem(element, selected) {
        element.classList.toggle("active", selected);
        element.setAttribute("aria-pressed", selected ? "true" : "false");
    }

    // read the answers currently chosen in the modal
    function getPairModalOptions() {
        let options = {};
        section.querySelectorAll(".productV13FormPairModalBodyGroupValuesItem.active").forEach(element => {
            options[element.dataset.group] = element.dataset.value;
        });
        return options;
    }

    /**
     * Point the modal at a pair and open it.
     * @param {Element} pairElement the .productV13FormPair the modal will write back to
     * @param {boolean} isNew true when the pair has no answers yet, which changes the wording
     */
    function openPairModal(pairElement, isNew) {
        pairModalTarget = pairElement;

        // preselect the pair's saved answers, or start from a clean slate for a new pair
        const saved = pairElement.getAttribute("options") ? JSON.parse(pairElement.getAttribute("options")) : {};
        section.querySelectorAll(".productV13FormPairModalBodyGroupValuesItem").forEach(element => {
            setPairModalItem(element, saved[element.dataset.group] === element.dataset.value);
        });

        // the wording tells the visitor whether they are adding or amending
        const position = Array.from(section.querySelectorAll(".productV13FormPair.active")).indexOf(pairElement) + 1;
        pairModal.querySelector(".productV13FormPairModalHeaderBodyTitle").textContent = isNew ? "Add another pair" : `Edit pair ${position}`;
        pairModal.querySelector(".productV13FormPairModalFooterSave").textContent = isNew ? "Add pair" : "Save pair";

        pairModal.showModal();
        document.querySelector("body").classList.add("noScroll");

        // gray out choices that are dead ends for this pair's current answers
        refreshPairModalDisabledOptions();

        // always open at the top, and recheck whether the body still scrolls
        pairModal.querySelector(".productV13FormPairModalBody").scrollTop = 0;
        refreshPairModalHint();
    }

    function closePairModal() {
        pairModal.close();
    }

    // open edit
    section.querySelectorAll(".productV13FormPairActionsEdit").forEach(element => {
        element.addEventListener("click", function () {
            openPairModal(this.closest(".productV13FormPair"), false);
        })
    });

    // close event
    pairModal.addEventListener("close", function () {
        document.querySelector("body").classList.remove("noScroll");

        // a pair with no options was opened fresh and then cancelled, so it goes back to unused
        if (pairModalTarget && !pairModalTarget.getAttribute("options")) {
            pairModalTarget.classList.remove("active");
        }

        pairModalTarget = null;
    });

    // trigger close modal
    pairModal.addEventListener("click", function (e) {
        if (e.target === this) {
            closePairModal();
        }
    });

    pairModal.querySelector(".productV13FormPairModalHeaderClose").addEventListener("click", closePairModal);

    // option clicks
    section.querySelectorAll(".productV13FormPairModalBodyGroupValuesItem").forEach(element => {
        element.addEventListener("click", function () {
            const group = this.dataset.group;

            // one answer per group
            section.querySelectorAll(`.productV13FormPairModalBodyGroupValuesItem[data-group="${group}"]`).forEach(item => setPairModalItem(item, false));
            setPairModalItem(this, true);

            refreshPairModalDisabledOptions();
        });
    });

    // the fade and chevron only make sense while there is more of the body to reach
    function refreshPairModalHint() {
        const body = pairModal.querySelector(".productV13FormPairModalBody");
        const more = (body.scrollHeight - body.scrollTop - body.clientHeight) > 8;
        pairModal.querySelector(".productV13FormPairModalHint").classList.toggle("active", more);
    }

    pairModal.querySelector(".productV13FormPairModalBody").addEventListener("scroll", refreshPairModalHint, {passive: true});
    window.addEventListener("resize", refreshPairModalHint);

    // update pair
    pairModal.querySelector(".productV13FormPairModalContent").addEventListener("submit", function (e) {
        e.preventDefault();

        // set this option as active
        pairModalTarget.classList.add("active");
        pairModalTarget.setAttribute("options", JSON.stringify(getPairModalOptions()));

        closePairModal();
        updateProduct();
    });

    // remove
    section.querySelectorAll(".productV13FormPairActionsRemove").forEach(element => {
        element.addEventListener("click", function () {
            // save pair element
            let pairElement = this.closest(".productV13FormPair");

            // remove active and options
            pairElement.classList.remove("active");
            pairElement.removeAttribute("options");

            // update product
            updateProduct();

            // check if there are no more left, reset the swiper to slide 1 and remove active classes on lebels
            if (section.querySelectorAll(".productV13FormPair.active").length === 0) {
                section.querySelector(".productV13FormSwiper").classList.add("active");
                section.querySelector(".productV13FormForm").classList.remove("active");
                section.querySelectorAll(".productV13FormOptionSlideLabel.active").forEach(element => {
                    element.classList.remove("active");
                    element.setAttribute("aria-pressed", "false");
                });
                productFormSwiper.slideTo(0);

                // with all answers cleared, recompute so only globally dead choices stay grayed
                refreshDisabledOptions();
            }
        });
    })

    // same as first
    section.querySelector(".productV13FormActionSame").addEventListener("click", function () {
        // check for a free slot
        let nextFree = section.querySelector(".productV13FormPair:not(.active)");
        if (nextFree) {
            nextFree.classList.add("active");
            nextFree.setAttribute("options", section.querySelector(".productV13FormPair").getAttribute("options"));
        }
        updateProduct();
    });

    // new pair
    section.querySelector(".productV13FormActionNew").addEventListener("click", function () {
        //check for a free slot
        let nextFree = section.querySelector(".productV13FormPair:not(.active)");
        if (nextFree) {
            nextFree.classList.add("active");
            openPairModal(nextFree, true);
        }
    });

    function updateProduct() {
        // reset atc button (atc active)
        section.querySelector(".productV13FormCheckoutATC").textContent = section.querySelector(".productV13FormCheckoutATC").dataset.atcText;
        section.querySelector(".productV13FormCheckoutATC").classList.add("active");
        section.querySelector(".productV13FormCheckoutATC").classList.remove("disabled");
        section.querySelector(".productV13FormCheckoutATC").classList.remove("message");

        // drop any back in stock state left over from the previous selection
        section.querySelector(".productV13FormCheckoutATC").classList.remove("OOS");
        delete section.querySelector(".productV13FormCheckoutATC").dataset.oosEvent;

        // hide error message, the out of stock path speaks through the atc button instead
        section.querySelector(".productV13FormError")?.classList.remove("active");


        // reset form data
        formData.items = [];
        section.querySelectorAll(".productV13FormPairBodyTitle")?.forEach(element => element.classList.remove("active"));
        // hide error message
        section.querySelector(".productV13FormCheckoutATC").classList.remove("active");
        section.querySelectorAll(".productV13FormPair.active").forEach((element, index) => {
            // reindex and restate this pair's answers
            let options = JSON.parse(element.getAttribute("options"));
            element.querySelector(".productV13FormPairBodyTitle").textContent = `PAIR ${index + 1}: ${options.sport}`;
            element.querySelector(".productV13FormPairBodySummary").textContent = `${options.gender} · size ${options.size} · ${options.weight} · age ${options.age}`;

            // add formdata
            let item = getFormData(options, product.handle);

            // check if item is not found (variant can't be found) or is out of stock
            if (item === false || !item.available) {

                // find that element
                let productFormPair = section.querySelector(`.productV13FormPair[options='${JSON.stringify(options)}']`);

                // indicate that this pair is invalid by setting title to active
                productFormPair.querySelector(".productV13FormPairBodyTitle").classList.add("active");

                // log request
                let logData = new FormData();
                logData.set("handle", product.handle);
                logData.set("options", JSON.stringify(options));
                fetch("https://api.vktry.com/variant-not-found.php", {
                    method: "POST",
                    body: logData
                });

                // we carry this variant but it is out of stock, so the atc becomes a back in stock signup
                if (item !== false) {
                    section.querySelector(".productV13FormCheckoutATC").textContent = "Notify me when back in stock";
                    section.querySelector(".productV13FormCheckoutATC").classList.add("OOS");

                    // re-resolve the variant so the event can carry what was actually sold out
                    const targetProduct = resolveTargetProduct(options.weight, options.age, options.gender);
                    const oosVariant = findVariant(targetProduct, options.size, sportToTopCover[options.sport], options.gender);

                    // stash the Gold OOS event properties so the click handler only has to add the email
                    section.querySelector(".productV13FormCheckoutATC").dataset.oosEvent = JSON.stringify({
                        "Product": targetProduct.title,
                        "Product Handle": targetProduct.handle,
                        "Variant ID": oosVariant.id,
                        "Variant SKU": oosVariant.sku,
                        "Variant Title": oosVariant.title,
                        "Size": options.size,
                        "Top Cover": oosVariant.option2,
                        "Gender": options.gender,
                        "Sport": options.sport,
                        "Weight": options.weight,
                        "Age": options.age
                    });

                    return;
                }

                // hide atc button
                section.querySelector(".productV13FormCheckoutATC").classList.add("active");

                // combinations we do not carry keep the error message instead
                section.querySelector('.productV13FormErrorMsg').textContent = "Unavailable — this selection is not currently part of our assortment";

                // show error message
                section.querySelector(".productV13FormError").classList.add("active");

                return;
            }


            // show atc
            section.querySelector(".productV13FormCheckoutATC").classList.remove("active");


            // hide error message
            section.querySelector(".productV13FormError")?.classList.remove("active");

            // check for duplicates (id and property, if you check the entire object, the id won't match)
            for (let i = 0; i < formData.items.length; i++) {
                if (formData.items[i].id === item.id && JSON.stringify(formData.items[i].properties) === JSON.stringify(item.properties)) {
                    formData.items[i].quantity++;
                    return;
                }
            }

            // add item to form data
            formData.items.push(item);

        });
    }

    // dynamic shoe size title
    section.querySelectorAll(".productV13FormOptionSlideLabel[data-group='gender']").forEach(element => {
        element.addEventListener("click", function () {
            // ignore blocked choices so the title only reflects real selections
            if (this.classList.contains("soldOut")) {
                return;
            }

            section.querySelector('.productV13FormOptionSlideTitleGender').textContent = element.dataset.value;
        })
    })

    // locate the red note inside the size slide (only present when shoe_size_note setting is filled)
    const sizeSlide = section.querySelector(".productV13FormOptionSlideLabel[data-group='size']")?.closest(".productV13FormOptionSlide");
    const sizeRedNote = sizeSlide?.querySelector(".productV13FormOptionSlideRed");

    // keep the original note so sports without their own note fall back to it
    const sizeRedNoteHTML = sizeRedNote?.innerHTML;

    // show sport specific notes on the size slide when the sport changes
    section.querySelectorAll(".productV13FormOptionSlideLabel[data-group='sport']").forEach(element => {
        element.addEventListener("click", function () {
            // ignore blocked choices so the note only reflects real selections
            if (this.classList.contains("soldOut")) {
                return;
            }

            // nothing to update when the shoe_size_note setting is empty
            if (!sizeRedNote) {
                return;
            }

            // restore the original note so the slide stays in sync with the current sport
            sizeRedNote.innerHTML = sizeRedNoteHTML;

            // ice hockey replaces the note with its own sizing instruction
            if (this.dataset.value === "Ice Hockey") {
                sizeRedNote.innerHTML = `<p>Size up .5 size for Ice Hockey</p>`;
                return;
            }

            // cheerleading keeps the original note and adds its own instruction below it
            if (this.dataset.value === "Cheerleading") {
                sizeRedNote.insertAdjacentHTML("beforeend", `<div class="productFormOptionSlideRedCheerleading">For use in Nfinity shoes, size down 2 sizes</div>`);
            }
        });
    });


    /**************
     Product ATC
     ***************/

    section.querySelector(".productV13FormCheckoutATC").addEventListener("click", async function () {
        // out of stock selections collect an email for the back in stock alert instead of adding to cart
        if (this.classList.contains("OOS")) {
            const oosEvent = JSON.parse(this.dataset.oosEvent);

            // Klaviyo OOS
            Swal.fire({
                title: `Notify Me When Available`,
                input: "email",
                inputPlaceholder: "Email",
                showCancelButton: true,
                confirmButtonText: "Notify Me",
                cancelButtonText: "Cancel",
                showLoaderOnConfirm: true,
                customClass: {
                    container: 'OOS'
                },
                preConfirm: async () => {
                    let email = Swal.getInput().value;

                    // identify first so the event lands on that profile instead of an anonymous one
                    await klaviyo.identify({
                        '$email': email
                    });

                    // track the event
                    klaviyo.push(["track", "Gold OOS", oosEvent]);
                },
                allowOutsideClick: () => !Swal.isLoading()
            }).then((result) => {
                // if the user confirmed, let them know the alert is set
                if (result.isConfirmed) {
                    Swal.fire({
                        title: `Success`,
                        text: `We'll notify you when the product is back in stock.`,
                        icon: "success"
                    })
                }
            });

            return;
        }

        // add loading animation
        this.classList.add("loading");


        // check for add socks
        if (addSocks) {

            const firstPair = JSON.parse(document.querySelector(".productV13FormPair.active").getAttribute("options"));
            const firstPairGender = firstPair.gender;
            const firstPairSize = firstPair.size;

            let variantID;
            // add 3 pack grip socks
            if ((firstPairGender === "Male" && isSizeInRange(firstPairSize, 0, 8)) || (firstPairGender === "Female" && isSizeInRange(firstPairSize, 0, 11))) {
                variantID = 52971011146095 // medium
            } else if ((firstPairGender === "Male" && isSizeInRange(firstPairSize, 0, 12)) || (firstPairGender === "Female" && isSizeInRange(firstPairSize, 0, 13))) {
                variantID = 52971011178863; // large
            } else {
                variantID = 52971011211631; // xlarge
            }

            formData.items.push({
                id: variantID,
                quantity: 1,
                properties: {}
            })
        }


        if (addHat) {
            formData.items.push({
                id: 52392563540335, // black hat
                quantity: 1,
                properties: {}
            })
        }

        // check for discount kit property
        if (discountKitProperty !== "") {
            formData.items.forEach(item => {
                item.properties._discount_kit = discountKitProperty;
            })
        }

        // add product
        await addProduct(formData);

        // stop loading
        this.classList.remove("loading");

        // check for upsell
        await openUpsell();

        // open cart
        await openCart();
    });

    /********************************** Shoe Size **********************************/


    // open shoe size modal
    document.querySelectorAll(".openShoeSize").forEach(element => {
        element.addEventListener("click", function () {
            // Find the correct shoe size modal

            // The modal to open is based on the sport
            // Gold: Basketball, fitness, racquet sports, running, volleyball, walking/hiking, everyday
            // Black: Baseball, boxing, football, golf, lacrosse, rugby, ultimate frisbee, [NEW -> ]Cheerleading, Track, Soccer
            // Red: REMOVED and uses black now, was Cheerleading, Track, Soccer
            let sport = document.querySelector(`.productV13FormOptionSlideLabel.active[data-group="sport"]`)?.dataset?.value;

            if (sport === "Basketball" || sport === "Fitness" || sport === "Tennis/Pickleball" || sport === "Running" || sport === "Volleyball" || sport === "Hiking" || sport === "Walking" || sport === "Other") {
                document.querySelector('#shoeSizeGold').showModal();
            } else {
                document.querySelector('#shoeSizeBlack').showModal();
            }

            document.querySelector("body").classList.add("noScroll");

        })
    });

    // open clog size modal
    document.querySelectorAll(".upsellV13Size")?.forEach(element => {
        element.addEventListener("click", function () {
            document.querySelector('#shoeSizeClog').showModal();
            document.querySelector("body").classList.add("noScroll");
        })
    });

    //close shoe size event listener
    document.querySelectorAll(".shoeSizeContentClose")?.forEach(function (element) {
        element.addEventListener("click", async function () {
            let target = this.dataset.target;
            await closeSize(target);

        });
    });

    //close shoe size event listener
    document.querySelectorAll(".shoeSize")?.forEach(function (element) {
        element.addEventListener("click", async function (e) {
            if (e.target === this) {
                let target = element.id;
                await closeSize(target);

            }
        });
    });


    // close shoe size
    function closeSize(target) {
        document.querySelector(`#${target}`).close();
        document.querySelector("body").classList.remove("noScroll");
    }

    // register dialogs
    // wait for document to load first
    document.addEventListener("DOMContentLoaded", function () {
        document.querySelectorAll("dialog.shoeSize")?.forEach(function (element) {
            dialogPolyfill.registerDialog(element);
        });
    });


    /**************
     Product Gift
     ***************/

    // listen for "Request as gift" click
    section.querySelector(".productV13ContentGiftLabelInput").addEventListener("click", function () {

        // show/hide the gift message in the ATC FORM
        section.querySelector(".productV13Gift").classList.remove("active");
        if (this.checked) {
            section.querySelector(".productV13Gift").classList.add("active");
        }
    });


    // Submit gift form
    section.querySelector('.productV13GiftFormSubmit').addEventListener('click', async function (e) {
        e.preventDefault();

        // create url params
        const giftParams = new URLSearchParams({
            fromName: document.querySelector(".productV13GiftFormName").value,
            handle: product.handle,
            formData: JSON.stringify(formData)
        }).toString();

        // send event to Klaviyo
        const response = await fetch(`https://api.vktry.com/send-a-gift.php`, {
            method: "POST",
            body: new URLSearchParams({
                fromName: document.querySelector(".productV13GiftFormName").value,
                toEmail: document.querySelector(".productV13GiftFormEmail").value,
                purchaseURL: `https://${window.location.hostname}/pages/send-gift?${giftParams}`,
            })
        })
            .then(response => response.json())

        // check for error
        if (response.error) {
            await Swal.fire({
                title: 'Error',
                text: response.error,
            });
            return;
        }

        // update text
        section.querySelector(".productV13GiftTxt").textContent = "Your email was sent with the VKTRYs you would like as a gift.";
        section.querySelector('.productV13GiftForm').classList.remove('active');
    });

    /**************
     Product Upsells
     ***************/

        // upsell swiper
    let upsellSwiper = new Swiper('.upsellV13Swiper', {
            slidesPerView: 1,
            spaceBetween: 10,
            pagination: {
                el: '.upsellV13SwiperFooterPag',
                clickable: true,
                renderBullet: function (index, className) {
                    return '<span class="' + className + '">' + (index + 1) + "</span>";
                },
            },
        });

    // store the swiper slides to use for later
    let socksSlide = document.querySelector('.upsellV13Socks');
    let boostsSlide = document.querySelector('.upsellV13Boosts');
    let gripsSlide = document.querySelector('.upsellV13Grips');
    let clogsSlide = document.querySelector('.upsellV13Clogs');
    let silverSlide = document.querySelector('.upsellV13Silver');
    let silverProduct = JSON.parse(document.querySelector('.upsellV13Silver').dataset.product)
    let additionalProperties;
    let tag = '';

    // map the sportToUpsell titles to the slides
    let upsellTitleToUpsellSlide = {
        "Ankle Boosts": boostsSlide,
        "Grip Socks": gripsSlide,
        "Propulsion Socks": socksSlide,
        "Recovery Clogs": clogsSlide,
        "Silver Insoles": silverSlide
    }

    // add to cart event listeners
    document.querySelectorAll(".upsellV13Checkout").forEach(element => {
        element.addEventListener("click", async function () {

            // Used in Discount Kit to target specific upsell products
            let discountProperty = '';

            // start loading
            this.classList.add("loading");

            // determine the first insole gender and size
            const firstPair = JSON.parse(document.querySelector(".productV13FormPair.active").getAttribute("options"));
            const firstPairGender = firstPair.gender;
            const firstPairSize = firstPair.size;

            // determine the variant id
            let variantID;
            let available = true;

            if (element.classList.contains("boosts")) {
                discountProperty = 'boosts_upsell';
                tag = 'boost upsell';
                if ((firstPairGender === "Male" && isSizeInRange(firstPairSize, 0, 9)) || (firstPairGender === "Female" && isSizeInRange(firstPairSize, 0, 10.5))) {
                    variantID = 42757218402475; // medium
                } else {
                    variantID = 42757218435243; // large
                }
            } else if (element.classList.contains("silver")) {
                discountProperty = 'silver_free_upsell';
                tag = 'silver upsell';
                let silverUpsellEl = document.querySelector('.upsellV13Silver');
                let formData = JSON.parse(this.dataset.form);
                variantID = formData.id;

                // save additional properties
                additionalProperties = formData.properties;

            } else if (element.classList.contains("clogs")) {

                discountProperty = 'clog_upsell';
                tag = 'clog upsell';
                let clogsEl = document.querySelector('.upsellV13Clogs');

                // find the correct clog OR slide variant
                let clogOrSlideProduct = JSON.parse(clogsEl.querySelector(".upsellV13ClogsOptionsGroupItemValuesLabelInput.type:checked").value);

                let color = clogsEl.querySelector(".upsellV13ClogsOptionsGroupItemValuesLabelInput.color:checked").value;

                // find new variant based on the options
                let clogVariant = clogOrSlideProduct.variants.find((variant) => {
                    return (
                        variant.options[0] === color &&
                        variant.options[1] === clogsEl.querySelector('.upsellV13OptionsSelect[name="Gender"]').value &&
                        variant.options[2] === clogsEl.querySelector('.upsellV13OptionsSelect[name="Size"]').value
                    );
                });


                if (clogVariant.available) {
                    variantID = clogVariant.id;
                } else {
                    available = false;
                }
            } else if (element.classList.contains("grips")) {
                tag = 'grips upsell';
                discountProperty = 'grip_socks_upsell';
                if ((firstPairGender === "Male" && isSizeInRange(firstPairSize, 0, 8)) || (firstPairGender === "Female" && isSizeInRange(firstPairSize, 0, 11))) {
                    variantID = 52140728320367 // medium
                } else if ((firstPairGender === "Male" && isSizeInRange(firstPairSize, 0, 12)) || (firstPairGender === "Female" && isSizeInRange(firstPairSize, 0, 13))) {
                    variantID = 52140728353135; // large
                } else {
                    variantID = 52140728385903; // xlarge
                }
            } else {
                // compression socks
                discountProperty = 'compression_socks_upsell';
                tag = 'compression socks upsell';
                if ((firstPairGender === "Male" && isSizeInRange(firstPairSize, 0, 7)) || (firstPairGender === "Female" && isSizeInRange(firstPairSize, 0, 9))) {
                    variantID = 42970044924075 // medium
                } else if ((firstPairGender === "Male" && isSizeInRange(firstPairSize, 0, 12)) || (firstPairGender === "Female" && isSizeInRange(firstPairSize, 0, 14))) {
                    variantID = 42970045382827; // large
                } else {
                    variantID = 42970045415595; // xlarge
                }
            }

            // if this has already been added, remove it from the cart
            if (this.classList.contains("added")) {
                let cart = await getCart();

                // find the item
                let item = cart.items.find(item => item.variant_id === variantID);

                // if there is no item, return
                if (!item) {
                    this.textContent = "Add to Cart";
                    this.classList.remove("loading");
                    return;
                }

                await changeProduct({
                    id: item.key,
                    quantity: item.quantity - 1
                });

                this.classList.remove("added");
                this.textContent = "Add to Cart";
                this.classList.remove("loading");
                return;
            }


            // add
            if (available) {
                let formData = {
                    quantity: 1,
                    id: variantID,
                    properties: {
                        _upsell: true,
                        _upsell_tag: tag,
                        _discount_kit: discountProperty
                    }
                }

                // add additional properties if they exist (used for silver upsell)
                if (additionalProperties) {
                    formData.properties = {...formData.properties, ...additionalProperties};
                }

                await addProduct(formData);
            }

            // stop loading
            this.classList.remove("loading");

            element.textContent = "Remove";
            this.classList.add("added");


            // if all upsells have been added, close
            if (document.querySelectorAll(".upsellV13Checkout.added").length === document.querySelectorAll(".upsellV13Checkout").length) {
                this.closest(".upsellV13").close();
            }

            // if there is a next slide, slide to it
            if (this.closest(".swiper-slide").nextElementSibling) {
                upsellSwiper.slideNext();
            }
        });
    });

    // controls
    document.querySelector('.upsellV13ContentControl.prev').addEventListener('click', function () {
        upsellSwiper.slidePrev();
    });

    document.querySelector('.upsellV13ContentControl.next').addEventListener('click', function () {
        upsellSwiper.slideNext();
    });

    /**************
     Gorgias chat
     ***************/
    document.querySelector('.productV13Chat')?.addEventListener('click', function () {
        // open gorgias chat
        if (GorgiasChat) {
            GorgiasChat.open()
        }

    })

    /********************************************** Upsell ***************************************/

        // top cover is based of weight (SILVER INSOLES)
    let weightToTopCover = {
            "70 - 99lbs": "L",
            "100 - 120lbs": "L",
            "121 - 150lbs": "L",
            "151 - 180lbs": "H",
            "181 - 210lbs": "H",
            "211 - 230lbs": "H",
            "231 - 260lbs": "H",
            "261lbs - Up": "H"
        };

    // Update Silver Upsell
    function updateSilverUpsell(autoselect = false) {
        let silverUpsellEl = document.querySelector('.upsellV13Silver');
        let firstPair = JSON.parse(document.querySelector(".productV13FormPair.active").getAttribute("options"));
        let firstPairSize = firstPair.size;


        // AutoSelect Age, weight, size, Gender, and sport if possible
        if (autoselect) {
            silverUpsellEl.querySelectorAll('.upsellV13OptionsSelect[name="weight"] option')?.forEach(option => option.selected = option.value === firstPair.weight);
            silverUpsellEl.querySelectorAll('.upsellV13OptionsSelect[name="age"] option')?.forEach(option => option.selected = option.value === firstPair.age);
            silverUpsellEl.querySelectorAll('.upsellV13OptionsSelect[name="gender"] option')?.forEach(option => option.selected = option.value === firstPair.gender);
            silverUpsellEl.querySelectorAll('.upsellV13OptionsSelect[name="sport"] option')?.forEach(option => option.selected = option.value === firstPair.sport);

            silverUpsellEl.querySelectorAll('.upsellV13OptionsSelect[name="size"] option')?.forEach(function (option) {
                let sizeSplit = option.value.split('-');
                if (sizeSplit[0] === firstPairSize || sizeSplit[1] === firstPairSize) {
                    option.selected = true;
                }
            })
        }

        // find the silver variant
        let silverWeight = silverUpsellEl.querySelector('.upsellV13OptionsSelect[name="weight"]').value;
        let silverAge = silverUpsellEl.querySelector('.upsellV13OptionsSelect[name="age"]').value;
        let silverSize = silverUpsellEl.querySelector('.upsellV13OptionsSelect[name="size"]').value;
        let silverGender = silverUpsellEl.querySelector('.upsellV13OptionsSelect[name="gender"]').value;
        let silverSport = silverUpsellEl.querySelector('.upsellV13OptionsSelect[name="sport"]').value;

        let topCover = weightToTopCover[silverWeight];

        if (
            silverGender === "Male" && silverSize === "5" ||
            silverGender === "Male" && silverSize === "5.5" ||
            silverGender === "Female" && silverSize === "7" ||
            silverGender === "Female" && silverSize === "7.5" ||
            silverGender === "Male" && silverSize === "6" ||
            silverGender === "Male" && silverSize === "6.5" ||
            silverGender === "Female" && silverSize === "8" ||
            silverGender === "Female" && silverSize === "8.5"

        ) {
            topCover = "L";
        }

        // 43-L -> 43-H
        // 44-L -> 44-H
        // 45-L -> 45-H
        if (
            silverGender === "Male" && silverSize === "11" ||
            silverGender === "Male" && silverSize === "11.5" ||
            silverGender === "Female" && silverSize === "13" ||
            silverGender === "Female" && silverSize === "13.5" ||
            silverGender === "Male" && silverSize === "12" ||
            silverGender === "Male" && silverSize === "12.5" ||
            silverGender === "Male" && silverSize === "13" ||
            silverGender === "Male" && silverSize === "13.5"
        ) {
            topCover = "H";
        }

        // athletes over 50 and 151-180 receive a topCover of L
        if (silverAge === "50 and Up" && silverWeight === "151 - 180lbs") {
            topCover = "L";
        }

        // option order is size, top cover, gender
        // find the correct variant based on the active options
        let variant = null;
        for (let i = 0; i < silverProduct.variants.length; i++) {

            // here comes the tricky part, Shopfiy might have 5-5.5 OR 5.5-6 ending on the top cover however we're going to receive a size of 5, 5.5 or 6.
            // We need to do a split and find an exact match
            if (
                // size
                silverProduct.variants[i].option1.split("-").includes(silverSize) &&
                // top cover
                silverProduct.variants[i].option2 === topCover &&
                // gender
                silverProduct.variants[i].option3 === silverGender
            ) {
                variant = silverProduct.variants[i];
                break;
            }
        }

        // save formData to the checkout button
        let formData = {
            quantity: 1,
            id: variant ? variant.id : null,
            properties: {
                "Age": silverAge,
                "Sport": silverSport,
                "Weight": silverWeight
            },
        }

        // save the form data
        formData = JSON.stringify(formData);
        silverUpsellEl.querySelector('.upsellV13Checkout.silver').setAttribute('data-form', formData);

        // no variant found
        if (!variant) {
            silverUpsellEl.querySelector('.upsellV13Checkout.silver').disabled = true;
            silverUpsellEl.querySelector('.upsellV13Checkout.silver').textContent = "Not Available";
            return;
        }
        let silverUpsell = variant;

        // unavailable variant
        if (!silverUpsell.available) {
            silverUpsellEl.querySelector('.upsellV13Checkout.silver').disabled = true;
            silverUpsellEl.querySelector('.upsellV13Checkout.silver').textContent = "Sold Out";
            return;
        }

        silverUpsellEl.querySelector('.upsellV13Checkout.silver').disabled = false;
        silverUpsellEl.querySelector('.upsellV13Checkout.silver').textContent = "Add To Cart";
    }


    function isSizeInRange(currentSize, min, max) {
        const range = currentSize.split('-').map(parseFloat);
        const size = parseFloat(range[0]);
        return !isNaN(size) && size >= min && size <= max;
    }

    // open ankle upsell
    async function openUpsell() {

        // get sport
        let options = JSON.parse(section.querySelector(`.productV13FormPair.active`).getAttribute("options"));

        let sport = options.sport;

        // get the sport to upsell slides
        let slides = sportToUpsell[sport]

        // if there are no slides, return
        if (!slides) {
            return;
        }


        // update swiper controls
        document.querySelectorAll('.upsellV13ContentControl').forEach(element => element.classList.remove("active"));
        document.querySelector('.upsellV13SwiperFooter').classList.remove("active");

        if (slides.length > 1) {
            document.querySelectorAll('.upsellV13ContentControl').forEach(element => element.classList.add("active"));
            document.querySelector('.upsellV13SwiperFooter').classList.add("active");
        }

        // remove all swiper slides
        upsellSwiper.removeAllSlides();

        // Show silver for the free silver template
        if (showSilver) {
            upsellSwiper.appendSlide(upsellTitleToUpsellSlide['Silver Insoles']);
            // listen for silver upsell input change
            document.querySelectorAll('.upsellV13OptionsSelect.silver')?.forEach(element => {
                element.addEventListener("change", function () {
                    updateSilverUpsell();
                });
            })

            // initial silver update with autoselect
            updateSilverUpsell(true);
        }

        // add in the correct slides
        for (let slide of slides) {
            upsellSwiper.appendSlide(upsellTitleToUpsellSlide[slide]);
        }

        // check if clog is available in selected variant, if not disable
        let firstPair = JSON.parse(document.querySelector(".productV13FormPair.active").getAttribute("options"));
        let firstPairGender = firstPair.gender;
        let firstPairSize = firstPair.size;
        let clogElement = document.querySelector('.upsellV13Clogs');

        // find the variant based on the selected input
        let color = clogElement.querySelector('.upsellV13ClogsOptionsGroupItemValuesLabelInput.color:checked')?.value;
        let clogOrSlideProduct = JSON.parse(clogElement.querySelector('.upsellV13ClogsOptionsGroupItemValuesLabelInput.type:checked').value);

        // auto select the correct gender and size
        clogElement.querySelectorAll('.upsellV13OptionsSelect[name="Gender"] option').forEach(option => option.selected = option.value === firstPairGender);
        clogElement.querySelectorAll('.upsellV13OptionsSelect[name="Size"] option').forEach(function (option) {
            let sizeSplit = option.value.split('-');
            if (sizeSplit[0] === firstPairSize || sizeSplit[1] === firstPairSize) {
                option.selected = true;
            }
        })

        // update titles
        const idToType = {
            "14723692167535": "Recovery Clogs",
            "14804854833519": "Recovery Slides"
        }
        clogElement.querySelector('.upsellV13ClogsOptionsGroupItemControlsTitleVal.type').textContent = idToType[clogOrSlideProduct.id];
        clogElement.querySelector('.upsellV13ClogsOptionsGroupItemControlsTitleVal.color').textContent = color;


        // find new variant based on the selected clog options
        let clogVariant = clogOrSlideProduct.variants.find((variant) => {
            return (
                variant.options[0] === color &&
                variant.options[1] === clogElement.querySelector('.upsellV13OptionsSelect[name="Gender"]').value &&
                variant.options[2] === clogElement.querySelector('.upsellV13OptionsSelect[name="Size"]').value
            );
        });


        if (!clogVariant) {
            // disable the clog upsell
            document.querySelector('.upsellV13Clogs').querySelector('.upsellV13Checkout').disabled = true;
            document.querySelector('.upsellV13Clogs').querySelector('.upsellV13Checkout').textContent = 'Unavailable';
        }


        let iconMap = {
            Clogs: {
                Black: "https://cdn.shopify.com/s/files/1/0072/5568/7283/files/image_3_339c99fe-96d3-4243-ae96-9360c0497e76.png?v=1760397014&width=500",
                White: "https://cdn.shopify.com/s/files/1/0072/5568/7283/files/Render.bip.1681_d2c3befd-4cef-4a7d-b217-e3af07d0151f.png?v=1764094893&width=500"
            },
            Slides: {
                Black: "https://cdn.shopify.com/s/files/1/0072/5568/7283/files/2025_C3_Render_recovery_CLOG.1667_2.png?v=1760397119&width=500",
                White: "https://cdn.shopify.com/s/files/1/0072/5568/7283/files/2025_C3_Render_recovery_CLOG.1666_25369c37-7037-40e7-9b11-e205618c3330.png?v=1764094893&width=500"
            }
        };


        // set the initial footwear availability before the upsell is shown
        syncClogsUpsellOptions();

        // open the upsell
        document.querySelector(`.upsellV13`).showModal();

        document.querySelector("body").classList.add("noScroll");

        // wait for modal to be closed
        await new Promise((resolve, reject) => {
            document.querySelector(`.upsellV13`).addEventListener("close", function (e) {
                resolve('closed');
            });
        });
    }

    // clog upsell options changes
    document.querySelectorAll('.upsellV13Clogs select, .upsellV13Clogs input').forEach(element => {
        element.addEventListener('change', (e) => {
            updateClogsUpsell();
        })
    })

    // Recovery Slides don't come in these colors, so hide whichever option conflicts with the current selection
    const SLIDES_UNAVAILABLE_COLORS = ["Fresh Navy", "Rebound Gray", "Overtime Orange", "Crisp Khaki"];
    function syncClogsUpsellOptions() {
        let clogElement = document.querySelector('.upsellV13Clogs');
        let color = clogElement.querySelector('.upsellV13ClogsOptionsGroupItemValuesLabelInput.color:checked')?.value;
        let type = clogElement.querySelector('.upsellV13ClogsOptionsGroupItemValuesLabelInput.type:checked')?.dataset.title;

        // hide the slides type while a color slides don't come in is chosen
        let slidesInput = clogElement.querySelector('.upsellV13ClogsOptionsGroupItemValuesLabelInput.type[data-title="Recovery Slides"]');
        if (slidesInput) {
            let slidesLabel = slidesInput.closest('.upsellV13ClogsOptionsGroupItemValuesLabel');
            slidesLabel.classList.add("active");
            if (SLIDES_UNAVAILABLE_COLORS.includes(color) && !slidesInput.checked) {
                slidesLabel.classList.remove("active");
            }
        }

        // hide the colors slides don't come in while the slides type is chosen
        SLIDES_UNAVAILABLE_COLORS.forEach(unavailableColor => {
            let colorInput = clogElement.querySelector(`.upsellV13ClogsOptionsGroupItemValuesLabelInput.color[value="${unavailableColor}"]`);
            if (!colorInput) {
                return;
            }
            let colorLabel = colorInput.closest('.upsellV13ClogsOptionsGroupItemValuesLabel');
            colorLabel.classList.add("active");
            if (type === "Recovery Slides" && !colorInput.checked) {
                colorLabel.classList.remove("active");
            }
        });
    }

    // update the clogs ATC based on the selected clog inputs
    function updateClogsUpsell() {
        let clogElement = document.querySelector('.upsellV13Clogs');

        // the chosen color/type may make the other option unavailable
        syncClogsUpsellOptions();


        // find the variant based on the selected input
        let color = clogElement.querySelector('.upsellV13ClogsOptionsGroupItemValuesLabelInput.color:checked')?.value;
        let clogOrSlideProduct = JSON.parse(clogElement.querySelector('.upsellV13ClogsOptionsGroupItemValuesLabelInput.type:checked').value);


        // find new variant based on the selected clog options
        let clogVariant = clogOrSlideProduct.variants.find((variant) => {
            return (
                variant.options[0] === color &&
                variant.options[1] === clogElement.querySelector('.upsellV13OptionsSelect[name="Gender"]').value &&
                variant.options[2] === clogElement.querySelector('.upsellV13OptionsSelect[name="Size"]').value
            );
        });

        // update image
        clogElement.querySelector('.upsellV13Hero').src = clogVariant.featured_image.src + '&width=400';


        // update titles
        const idToType = {
            "14723692167535": "Recovery Clogs",
            "14804854833519": "Recovery Slides"
        }
        clogElement.querySelector('.upsellV13ClogsOptionsGroupItemControlsTitleVal.type').textContent = idToType[clogOrSlideProduct.id];
        clogElement.querySelector('.upsellV13ClogsOptionsGroupItemControlsTitleVal.color').textContent = color;


        if (!clogVariant) {
            // disable the clog upsell
            document.querySelector('.upsellV13Clogs').querySelector('.upsellV13Checkout').disabled = true;
            document.querySelector('.upsellV13Clogs').querySelector('.upsellV13Checkout').textContent = 'Unavailable';
            return
        }

        document.querySelector('.upsellV13Clogs').querySelector('.upsellV13Checkout').disabled = false;
        let total = price(document.querySelector('.upsellV13Checkout.clogs').dataset.salePrice, true);
        document.querySelector('.upsellV13Clogs').querySelector('.upsellV13Checkout').textContent = `Add to Cart - ${total}`;
    }


    // close events
    document.querySelectorAll(".upsellV13").forEach(element => {
        // exist backdrop
        element.addEventListener("click", function (e) {
            if (e.target === this) {
                this.close();
                document.querySelector("body").classList.remove("noScroll");

            }
        })
    });

    // close upsell via button
    document.querySelectorAll(".upsellV13Close").forEach(element => {
        element.addEventListener("click", function () {
            this.closest(".upsellV13").close();
            document.querySelector("body").classList.remove("noScroll");
        });
    });


    // register dialog
    dialogPolyfill.registerDialog(document.querySelector(".upsellV13"));


    // delete the hidden upsells from the DOM
    document.querySelector('.upsellV13Hidden').remove();


    // sticky atc
    // if productContentBodyMain is not present, show sticky
    window.addEventListener("scroll", function () {
        let target = document.querySelector(".productV13ContentBodyMain");

        let rect = target.getBoundingClientRect();
        if (rect.top <= 0) {
            document.querySelector(".productV13Sticky").classList.add("active");
        } else {
            document.querySelector(".productV13Sticky").classList.remove("active");
        }

    });

    // listen for click
    document.querySelector(".productV13Sticky")?.addEventListener("click", function () {
        const targetPosition = document.querySelector('.productV13ContentBodyMain').getBoundingClientRect().top + window.scrollY - 120;

        // Scroll to the product form
        window.scrollTo({
            top: targetPosition,
            behavior: "smooth"
        });

    });

    // any link pointing at #product scrolls to the form instead of jumping to a missing anchor
    document.querySelectorAll(`a[href="#product"]`).forEach(element => {
        element.addEventListener("click", function (e) {
            e.preventDefault();

            const targetPosition = document.querySelector('.productV13ContentBodyMain').getBoundingClientRect().top + window.scrollY - 120;

            // Scroll to the product form
            window.scrollTo({
                top: targetPosition,
                behavior: "smooth"
            });

        });
    });


}())